import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone, CheckCircle2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/Brand";
import { http, formatApiError } from "@/lib/api";
import { COMPANY, SERVICES } from "@/lib/siteContent";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = { nome: "", email: "", telefono: "", organizzazione: "", servizio: "", messaggio: "", privacy: false, website: "" };

const Info = ({ icon: Icon, label, value, href, tid }) => (
  <div className="flex gap-3"><Icon className="mt-0.5 h-4 w-4 text-blue-600" /><div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    {href ? <a href={href} data-testid={tid} className="text-sm font-medium text-slate-800 underline decoration-slate-300 underline-offset-4 transition-colors hover:text-blue-600 hover:decoration-blue-600">{value}</a> : <p className="text-sm text-slate-800">{value}</p>}
  </div></div>
);

export default function Contatti() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...EMPTY, servizio: params.get("servizio") || "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) return toast.error("Devi accettare l'informativa privacy");
    setSending(true);
    try {
      await http.post("/public/contatti", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <SiteHeader />
      <main className="mx-auto grid w-full max-w-6xl flex-1 gap-12 px-6 py-16 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Contatti</p>
          <h1 className="mt-3 font-heading text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl">Parliamo del tuo condominio.</h1>
          <p className="mt-5 leading-relaxed text-slate-600">Compila il modulo: ti ricontatteremo per una consulenza o un preventivo.</p>
          <div className="mt-10 space-y-5">
            <Info icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} tid="contact-email-link" />
            <Info icon={Phone} label="Telefono" value={COMPANY.phone} href={`tel:+39${COMPANY.phone.replace(/\s/g, "")}`} tid="contact-phone-link" />
            <Info icon={MapPin} label="Sede" value={`${COMPANY.legalName} · ${COMPANY.address}`} />
          </div>
        </div>
        {sent ? (
          <div data-testid="contact-success" className="fade-up flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <p className="mt-4 font-heading text-xl font-bold text-slate-900">Richiesta inviata</p>
            <p className="mt-1 text-sm text-slate-500">Grazie, ti ricontatteremo al più presto.</p>
            <button onClick={() => setSent(false)} data-testid="contact-new-button" className="mt-6 text-sm font-medium text-blue-600 hover:underline">Invia un'altra richiesta</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="fade-up grid gap-5 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2 sm:p-8">
            <div className="space-y-1.5"><Label htmlFor="nome">Nome e cognome *</Label><Input id="nome" required value={form.nome} onChange={set("nome")} data-testid="contact-nome-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="email">Email *</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="tel">Telefono</Label><Input id="tel" value={form.telefono} onChange={set("telefono")} data-testid="contact-telefono-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="org">Studio / Condominio</Label><Input id="org" value={form.organizzazione} onChange={set("organizzazione")} data-testid="contact-organizzazione-input" /></div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="serv">Servizio di interesse</Label>
              <select id="serv" value={form.servizio} onChange={set("servizio")} data-testid="contact-servizio-select" className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:border-slate-900">
                <option value="">Informazioni generali</option>
                {SERVICES.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
              </select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="msg">Messaggio *</Label><Textarea id="msg" required rows={5} value={form.messaggio} onChange={set("messaggio")} data-testid="contact-messaggio-input" /></div>
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <label className="flex items-start gap-3 text-sm text-slate-600 sm:col-span-2">
              <Checkbox checked={form.privacy} onCheckedChange={(v) => setForm({ ...form, privacy: !!v })} data-testid="contact-privacy-checkbox" className="mt-0.5" />
              Ho letto l'informativa e acconsento al trattamento dei miei dati personali per essere ricontattato, ai sensi del Reg. UE 2016/679.
            </label>
            <button type="submit" disabled={sending} data-testid="contact-submit-button" className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-slate-900 px-6 font-medium text-white transition-colors hover:bg-blue-600 disabled:opacity-60 sm:col-span-2 sm:justify-self-start">
              {sending && <Loader2 className="h-4 w-4 animate-spin" />} Invia richiesta
            </button>
          </form>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
